import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import './LayerContextMenu.css';
import { normalizeBattleLayerId } from '../../utils/battleRoomLayerUtils';

/**
 * LayerContextMenu Component - Right-Click Asset Management Menu
 * 
 * This component provides a context menu that appears when users right-click on
 * tokens or map assets. It allows for quick asset management and layer operations.
 * 
 * WHAT IT PROVIDES:
 * - Shows which layer the clicked asset is currently on
 * - Allows moving assets between different layers (Map, Token, GameMaster)
 * - Provides delete option to remove unwanted assets from the battle map
 * - Appears at the exact mouse position where the right-click occurred
 * 
 * MENU OPTIONS:
 * - "Currently on [Layer Name]" - Shows current layer assignment
 * - "Move to Map Layer" - Moves asset to background map layer
 * - "Move to Token Layer" - Moves asset to character/monster layer
 * - "Move to GameMaster Layer" - Moves asset to GM-only layer
 * - "Delete Asset" - Permanently removes the asset from the battle map
 * 
 * HOW IT WORKS:
 * - Triggered by right-click events on Token or MapArea components
 * - Positioned absolutely at the mouse cursor location
 * - Automatically closes when user clicks elsewhere or selects an option
 * - Calls appropriate handlers to actually perform the layer moves or deletions
 * 
 * INTEGRATION:
 * - Used by Token.js and MapArea.js components for right-click handling
 * - Receives asset information (type, ID, current layer) from parent components
 * - Calls onLayerChange and onDelete handlers provided by BattleRoom
 * - Styled to match the overall Roll20-style dark theme
 */

const LayerContextMenu = ({ 
  x, 
  y, 
  visible, 
  assetType, 
  assetId, 
  currentLayer, 
  onLayerChange, 
  onDelete, 
  onProperties,
  onClose,
  // @date_of_change: 2025-10-05 - Ownership controls for GM assignment
  isGameMaster = false,
  connectedPlayers = [],
  getTokenControlInfo = () => ({ controlled: false, displayText: 'Unassigned', color: 'gray' }),
  onAssignOwnership = null,
  onUnassignOwnership = null,
  // @date_of_change: 2025-10-06 - Role-gated Properties visibility
  // When false, hide the Properties option for non-GM players or unauthorized users
  canOpenProperties = true
}) => {
  const menuRef = useRef();
  // @date_of_change: 2025-10-07 - Nested submenu for ownership assignment
  const [showOwnershipSubmenu, setShowOwnershipSubmenu] = React.useState(false);

  // Helper functions for layer information
  const getLayerIcon = (layerId) => {
    const normalizedLayerId = normalizeBattleLayerId(layerId, layerId);
    const layerIcons = {
      'map': '🗺️',
      'token': '👤', 
      'gamemaster': '🎲'
    };
    return layerIcons[normalizedLayerId] || '📋';
  };

  const getLayerName = (layerId) => {
    const normalizedLayerId = normalizeBattleLayerId(layerId, layerId);
    const layerNames = {
      'map': 'Map Layer',
      'token': 'Token Layer',
      'gamemaster': 'GameMaster Layer'
    };
    return layerNames[normalizedLayerId] || normalizedLayerId;
  };

  const getAvailableLayers = () => {
    return [
      ['map', { id: 'map', name: 'Map Layer', icon: '🗺️' }],
      ['token', { id: 'token', name: 'Token Layer', icon: '👤' }],
      ['gamemaster', { id: 'gamemaster', name: 'GameMaster Layer', icon: '🎲' }]
    ];
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible, onClose]);

  const position = useMemo(() => {
    const fallbackX = Number.isFinite(x) ? x : 0;
    const fallbackY = Number.isFinite(y) ? y : 0;

    if (typeof window === 'undefined') {
      return { x: fallbackX, y: fallbackY };
    }

    const viewportWidth = window.innerWidth || 0;
    const viewportHeight = window.innerHeight || 0;
    const estimatedWidth = 260;
    const estimatedHeight = 420;
    const margin = 8;

    return {
      x: viewportWidth
        ? Math.min(Math.max(margin, fallbackX), Math.max(margin, viewportWidth - estimatedWidth - margin))
        : fallbackX,
      y: viewportHeight
        ? Math.min(Math.max(margin, fallbackY), Math.max(margin, viewportHeight - estimatedHeight - margin))
        : fallbackY
    };
  }, [x, y]);

  if (!visible) return null;

  // @date_of_change: 2025-10-06 - Precompute control info for token ownership UI
  // Avoid repeated calls and enable consistent styling/indicators
  const controlInfo = (assetType === 'token' && typeof getTokenControlInfo === 'function' && assetId)
    ? getTokenControlInfo(assetId)
    : null;

  const menuContent = (
    <div 
      ref={menuRef}
      className="layer-context-menu"
      style={{
        left: position.x,
        top: position.y,
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="context-menu-header">
        <span className="current-layer-label">Current Layer:</span>
        <span>
          {getLayerIcon(currentLayer)} {getLayerName(currentLayer)}
        </span>
      </div>
      
      <div className="context-menu-divider"></div>

      {/* Read-only ownership status for players */}
      {assetType === 'token' && !isGameMaster && controlInfo && (
        <div className="context-menu-section">
          <div className="context-menu-item" style={{ cursor: 'default' }}>
            <span className="layer-icon">🎮</span>
            <span className="layer-name">Control: {controlInfo.displayText}</span>
          </div>
        </div>
      )}

      {assetType === 'token' && !isGameMaster && (
        <div className="context-menu-divider"></div>
      )}

      {/* @date_of_change: 2025-10-05 - GM ownership assignment controls */}
      {/* @date_of_change: 2025-10-07 - Nested submenu for assignments */}
      {assetType === 'token' && isGameMaster && (
        <div className="context-menu-section">
          {/* Main item showing current control */}
          <div 
            className="context-menu-item"
            onMouseEnter={() => setShowOwnershipSubmenu(true)}
            onMouseLeave={() => setShowOwnershipSubmenu(false)}
            style={{ position: 'relative' }}
          >
            <span className="layer-icon">🎮</span>
            <span className="layer-name">
              Assign Control
              {controlInfo && ` (${controlInfo.displayText})`}
            </span>
            <span style={{ marginLeft: 'auto' }}>▶</span>
            
            {/* Submenu */}
            {showOwnershipSubmenu && (
              <div 
                className="layer-context-submenu"
                style={{
                  position: 'absolute',
                  left: '100%',
                  top: 0,
                  minWidth: '200px',
                  background: '#2c2f33',
                  border: '1px solid #4a4d52',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  zIndex: 1001
                }}
              >
                {/* Assign to specific player */}
                {Array.isArray(connectedPlayers) && connectedPlayers.length > 0 && connectedPlayers.map((p) => (
                  <div
                    key={p.userId}
                    className="context-menu-item"
                    onClick={() => {
                      if (typeof onAssignOwnership === 'function') {
                        onAssignOwnership('player', p.userId);
                      }
                      setShowOwnershipSubmenu(false);
                    }}
                  >
                    <span className="layer-icon">👤</span>
                    <span className="layer-name">{p.displayName || p.userId}</span>
                    {controlInfo?.userId === p.userId && (
                      <span className="current-indicator">✓</span>
                    )}
                  </div>
                ))}

                {/* Assign to all players */}
                <div
                  className="context-menu-item"
                  onClick={() => {
                    if (typeof onAssignOwnership === 'function') {
                      onAssignOwnership('all', null);
                    }
                    setShowOwnershipSubmenu(false);
                  }}
                >
                  <span className="layer-icon">🌐</span>
                  <span className="layer-name">All Players</span>
                  {controlInfo && controlInfo.displayText === 'All Players' && (
                    <span className="current-indicator">✓</span>
                  )}
                </div>

                {/* GM-only control */}
                <div
                  className="context-menu-item"
                  onClick={() => {
                    if (typeof onAssignOwnership === 'function') {
                      onAssignOwnership('gm', null);
                    }
                    setShowOwnershipSubmenu(false);
                  }}
                >
                  <span className="layer-icon">🎲</span>
                  <span className="layer-name">GM Only</span>
                  {controlInfo && controlInfo.displayText === 'Game Master' && (
                    <span className="current-indicator">✓</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {assetType === 'token' && isGameMaster && (
        <div className="context-menu-divider"></div>
      )}

      {isGameMaster && (
        <>
          <div className="context-menu-section">
            <div className="section-label">Move to Layer:</div>
            {getAvailableLayers().map(([layerId, layer]) => {
              const isCurrentLayer = normalizeBattleLayerId(layerId) === normalizeBattleLayerId(currentLayer, currentLayer);
              return (
                <div
                  key={layerId}
                  className="context-menu-item"
                  onClick={() => {
                    if (!isCurrentLayer) {
                      onLayerChange(layerId);
                    }
                    onClose();
                  }}
                >
                  <span className="layer-icon">{layer.icon}</span>
                  <span className="layer-name">{layer.name}</span>
                  {isCurrentLayer && <span className="current-indicator">✓</span>}
                </div>
              );
            })}
          </div>
          
          <div className="context-menu-divider"></div>
        </>
      )}
      
      <div className="context-menu-section">
        {/* @date_of_change: 2025-10-06 - Gate Properties for players/non-owners */}
        {/* Show Properties only for tokens and when allowed by role/ownership */}
        {assetType === 'token' && canOpenProperties && (
          <div
            className="context-menu-item properties-item"
            onClick={() => {
              if (onProperties) {
                onProperties(assetId);
              }
              onClose();
            }}
          >
            <span className="properties-icon">🎭</span>
            <span className="properties-label">Properties</span>
          </div>
        )}
        
        <div
          className="context-menu-item delete-item"
          onClick={() => {
            if (onDelete) {
              onDelete();
            }
            onClose();
          }}
        >
          <span className="delete-icon">🗑️</span>
          <span className="delete-label">Delete Asset</span>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return menuContent;
  }

  return createPortal(menuContent, document.body);
};

export default LayerContextMenu;
